import React, { useState } from 'react';
import { ProjectCreationEligibility } from '../../services/projectCreationSettingsService';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { LinkedInAuthService } from '../../services/linkedinAuthService';
import ErrorDisplay from '../ui/ErrorDisplay';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import { ErrorFactory } from '../../utils/errorFactory';
import { AppError } from '../../types/error';
import '../../styles/components/ProjectCreationRequirements.css';

interface ProjectCreationRequirementsProps {
  eligibility: ProjectCreationEligibility;
  error?: AppError;
  onRetry?: () => void;
  showRetryButton?: boolean;
}

const ProjectCreationRequirements: React.FC<ProjectCreationRequirementsProps> = ({ 
  eligibility, 
  error,
  onRetry,
  showRetryButton = true
}) => {
  const { signInWithProvider } = useAuth();
  const { t } = useLanguage();
  const [linkedinConnecting, setLinkedinConnecting] = useState(false);
  const [linkedinError, setLinkedinError] = useState<string | null>(null);

  const { executeWithErrorHandling } = useErrorHandling();

  const handleLinkedInConnect = async () => {
    setLinkedinConnecting(true);
    setLinkedinError(null);
    
    const result = await executeWithErrorHandling(
      async () => {
        const authResult = await LinkedInAuthService.connectLinkedIn();
        if (!authResult.success) {
          throw authResult.error || ErrorFactory.createAuthenticationError(
            'LINKEDIN_AUTH_FAILED',
            'Failed to connect LinkedIn account',
            'LinkedIn authentication failed'
          );
        }
        return authResult;
      },
      'linkedin_connect'
    );

    if (!result) {
      setLinkedinError('Failed to connect LinkedIn. Please try again.');
    }
    
    setLinkedinConnecting(false);
  };

  if (error) {
    return (
      <div className="project-creation-requirements">
        <ErrorDisplay
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div className="project-creation-requirements">
      <div className="card border-warning">
        <div className="card-header bg-warning bg-opacity-10">
          <div className="d-flex align-items-center">
            <i className="bi bi-lock-fill text-warning me-2"></i>
            <h3 className="card-title mb-0">{t('createProject.requirementsTitle')}</h3>
          </div>
        </div>
        
        <div className="card-body">
          <p className="card-text mb-4">
            {t('createProject.requirementsDescription')}
          </p>

          <div className="requirements-list">
            {eligibility.reasons.insufficientPoints && (
              <div className="requirement-item mb-4 p-3 border rounded">
                <div className="d-flex align-items-center mb-3">
                  <div className="requirement-icon me-3">
                    <i className="bi bi-star-fill text-warning fs-4"></i>
                  </div>
                  <div className="requirement-content flex-grow-1">
                    <h4 className="mb-1">{t('createProject.pointsRequirement')}</h4>
                    <p className="text-muted mb-0">
                      {t('createProject.pointsRequirementDesc')}
                    </p>
                  </div>
                  <div className="requirement-status">
                    <i className="bi bi-x-circle-fill text-danger fs-5" title="Not met"></i>
                  </div>
                </div>
                
                <div className="points-status mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">
                      {eligibility.reasons.insufficientPoints.current.toLocaleString()} / {eligibility.reasons.insufficientPoints.required.toLocaleString()} {t('common.points')}
                    </span>
                    <span className="badge bg-warning text-dark">
                      {t('createProject.moreNeeded', { count: (eligibility.reasons.insufficientPoints.required - eligibility.reasons.insufficientPoints.current).toLocaleString() })}
                    </span>
                  </div>
                  
                  <div className="progress" style={{ height: '12px' }}>
                    <div 
                      className="progress-bar bg-warning"
                      role="progressbar"
                      style={{
                        width: `${Math.min(100, (eligibility.reasons.insufficientPoints.current / eligibility.reasons.insufficientPoints.required) * 100)}%`
                      }}
                      aria-valuenow={eligibility.reasons.insufficientPoints.current}
                      aria-valuemin={0}
                      aria-valuemax={eligibility.reasons.insufficientPoints.required}
                      title={`${Math.round((eligibility.reasons.insufficientPoints.current / eligibility.reasons.insufficientPoints.required) * 100)}% complete`}
                    />
                  </div>
                  
                  <div className="d-flex justify-content-between mt-2">
                    <small className="text-muted">
                      {t('createProject.percentComplete', { percent: Math.round((eligibility.reasons.insufficientPoints.current / eligibility.reasons.insufficientPoints.required) * 100) })}
                    </small>
                    <small className="text-muted">
                      {eligibility.reasons.insufficientPoints.current > 0 ? 
                        t('createProject.moreTasksEstimated', { count: Math.round((eligibility.reasons.insufficientPoints.required - eligibility.reasons.insufficientPoints.current) / (eligibility.reasons.insufficientPoints.current / 100)) }) :
                        t('createProject.startEarningPoints')
                      }
                    </small>
                  </div>
                </div>
                
                <div className="requirement-actions">
                  <a href="/tasks" className="btn btn-warning">
                    <i className="bi bi-trophy me-2"></i>
                    {t('createProject.earnMorePoints')}
                  </a>
                  <a href="/points-history" className="btn btn-outline-secondary ms-2">
                    <i className="bi bi-clock-history me-2"></i>
                    {t('createProject.viewPointsHistory')}
                  </a>
                  <a href="/questionnaires" className="btn btn-outline-warning ms-2">
                    <i className="bi bi-clipboard-check me-2"></i>
                    {t('createProject.completeSurveys')}
                  </a>
                </div>
              </div>
            )}

            {eligibility.reasons.linkedinRequired && (
              <div className="requirement-item mb-4 p-3 border rounded">
                <div className="d-flex align-items-center mb-3">
                  <div className="requirement-icon linkedin me-3">
                    <i className="bi bi-linkedin text-primary fs-4"></i>
                  </div>
                  <div className="requirement-content flex-grow-1">
                    <h4 className="mb-1">LinkedIn Authentication Required</h4>
                    <p className="text-muted mb-0">
                      Connect your LinkedIn account to verify your professional profile
                    </p>
                  </div>
                  <div className="requirement-status">
                    <i className="bi bi-x-circle-fill text-danger fs-5" title="Not met"></i>
                  </div>
                </div>
                
                <div className="linkedin-status mb-3">
                  <div className="alert alert-info mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Why LinkedIn?</strong> We require LinkedIn authentication to ensure 
                    project creators have verified professional profiles, which helps maintain 
                    the quality and credibility of projects on our platform.
                  </div>
                  
                  {linkedinError && (
                    <div className="alert alert-danger mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {linkedinError}
                    </div>
                  )}
                  
                  <div className="linkedin-benefits">
                    <h6 className="mb-2">Benefits of connecting LinkedIn:</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-1">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        Verify your professional identity
                      </li>
                      <li className="mb-1">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        Build trust with potential collaborators
                      </li>
                      <li className="mb-1">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        Access to premium project features
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="requirement-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleLinkedInConnect}
                    disabled={linkedinConnecting}
                  >
                    {linkedinConnecting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-linkedin me-2"></i>
                        Connect LinkedIn Account
                      </>
                    )}
                  </button>
                  <a href="/help" className="btn btn-outline-secondary ms-2">
                    <i className="bi bi-question-circle me-2"></i>
                    Learn More
                  </a>
                  <a href="/contact" className="btn btn-outline-info ms-2">
                    <i className="bi bi-envelope me-2"></i>
                    Need Help?
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreationRequirements;